import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const TaxDeadlineCountdown = () => {
  const { t, lang } = useLanguage();
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, deadline: null, isExtension: false });

  useEffect(() => {
    const calculateDeadline = () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      
      // Key tax deadlines
      const april15 = new Date(currentYear, 3, 15, 23, 59, 59); // April 15
      const october15 = new Date(currentYear, 9, 15, 23, 59, 59); // October 15 (extension)
      
      // If April 15 has passed but October 15 hasn't
      let targetDate = april15;
      let isExtension = false;
      
      if (now > april15 && now < october15) {
        targetDate = october15;
        isExtension = true;
      } else if (now > october15) {
        // Move to next year's April 15
        targetDate = new Date(currentYear + 1, 3, 15, 23, 59, 59);
        isExtension = false;
      }
      
      const diff = targetDate - now;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setCountdown({
        days,
        hours,
        minutes,
        deadline: targetDate,
        isExtension
      });
    };

    calculateDeadline();
    const interval = setInterval(calculateDeadline, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    const options = { month: 'long', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', options);
  };

  const isUrgent = countdown.days <= 30;
  const isCritical = countdown.days <= 7;

  return (
    <section className="py-5 px-6 relative overflow-hidden" style={{ background: '#0B1F3A' }}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full" style={{ background: '#D4AF37', filter: 'blur(80px)' }} />
        <div className="absolute bottom-0 left-1/4 w-48 h-48 rounded-full" style={{ background: '#2563EB', filter: 'blur(60px)' }} />
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
          {/* Left - Deadline Info */}
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isCritical ? 'animate-pulse' : ''}`} 
                 style={{ background: isCritical ? '#EF444420' : isUrgent ? '#D4AF3720' : '#2563EB20' }}>
              {isCritical ? (
                <AlertTriangle size={24} style={{ color: '#EF4444' }} />
              ) : (
                <Calendar size={24} style={{ color: isUrgent ? '#D4AF37' : '#60A5FA' }} />
              )}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider mb-0.5" 
                   style={{ color: isCritical ? '#EF4444' : '#D4AF37' }}>
                {countdown.isExtension 
                  ? (lang === 'es' ? 'Fecha Límite de Extensión' : 'Extension Deadline')
                  : (lang === 'es' ? 'Fecha Límite de Impuestos' : 'Tax Filing Deadline')
                }
              </div>
              <div className="text-white font-heading font-semibold text-lg">
                {formatDate(countdown.deadline)}
              </div>
            </div>
          </div>

          {/* Center - Countdown */}
          <div className="flex items-center gap-6">
            {[
              { value: countdown.days, label: lang === 'es' ? 'Días' : 'Days' },
              { value: countdown.hours, label: lang === 'es' ? 'Horas' : 'Hours' },
              { value: countdown.minutes, label: lang === 'es' ? 'Min' : 'Min' },
            ].map(({ value, label }, i) => (
              <React.Fragment key={label}>
                <div className="text-center">
                  <div className={`font-heading font-bold text-3xl md:text-4xl ${isCritical ? 'text-red-400' : 'text-white'}`}>
                    {String(value).padStart(2, '0')}
                  </div>
                  <div className="text-blue-300 text-xs uppercase tracking-wider">{label}</div>
                </div>
                {i < 2 && <div className="text-blue-400 font-heading text-2xl font-bold">:</div>}
              </React.Fragment>
            ))}
          </div>

          {/* Right - CTA */}
          <div className="flex items-center gap-4">
            {isUrgent && (
              <div className="hidden md:block text-right">
                <div className="text-blue-200 text-sm">
                  {lang === 'es' ? '¡No espere!' : "Don't wait!"}
                </div>
                <div className="text-white text-sm font-medium">
                  {lang === 'es' ? 'Presente a tiempo' : 'File on time'}
                </div>
              </div>
            )}
            <Link to="/book-appointment">
              <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                      style={{ background: '#D4AF37', color: '#0B1F3A' }}
                      data-testid="deadline-cta-btn">
                {lang === 'es' ? 'Reservar Ahora' : 'Book Now'}
                <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TaxDeadlineCountdown;
